import { useState } from 'react'
import { USER_DATE_YEAR_MAX, USER_DATE_YEAR_MIN, type PlanningPeriodKey } from '@crm/shared'
import { DateRangePopover } from '../../shared/ui/DateRangePopover'

const PERIODS: { key: PlanningPeriodKey; label: string }[] = [
  { key: 'month', label: 'Месяц' },
  { key: 'quarter', label: 'Квартал' },
  { key: 'year', label: 'Год' },
]

const QUARTERS = [1, 2, 3, 4] as const

function pad2(n: number): string {
  return String(n).padStart(2, '0')
}

const RU_MONTHS_SHORT = [
  'янв',
  'фев',
  'мар',
  'апр',
  'май',
  'июн',
  'июл',
  'авг',
  'сен',
  'окт',
  'ноя',
  'дек',
]

/** «2 янв – 24 мая» из двух YYYY-MM-DD. */
function rangeLabel(from: string | null, to: string | null): string {
  if (!from) return 'Период…'
  const fmt = (iso: string) => {
    const [, m, d] = iso.split('-').map(Number)
    return `${d} ${RU_MONTHS_SHORT[(m ?? 1) - 1]}`
  }
  return `${fmt(from)} – ${fmt(to ?? from)}`
}

export interface PlanningPeriodSelectorProps {
  periodKey: PlanningPeriodKey
  date: string | null
  from: string | null
  to: string | null
  onPeriodChange: (key: PlanningPeriodKey) => void
  onDateChange: (date: string) => void
  onCustomChange: (from: string, to: string) => void
}

export function PlanningPeriodSelector(props: PlanningPeriodSelectorProps) {
  const { periodKey, date, from, to, onPeriodChange, onDateChange, onCustomChange } = props
  const [pickerOpen, setPickerOpen] = useState(false)

  // Год/квартал из date=YYYY-MM (для quarter) или YYYY (для year).
  const year = date ? parseInt(date.slice(0, 4), 10) : new Date().getUTCFullYear()
  const quarterMonth = date ? parseInt(date.slice(5, 7), 10) : 1 // 1..12
  const quarter = Math.floor((quarterMonth - 1) / 3) + 1 // 1..4

  const setQuarter = (q: number, y: number = year) => onDateChange(`${y}-${pad2((q - 1) * 3 + 1)}`)

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <div className="inline-flex rounded-xl border border-gray-200 bg-white overflow-hidden">
        {PERIODS.map((p) => (
          <button
            key={p.key}
            onClick={() => onPeriodChange(p.key)}
            className={`px-3 py-1.5 text-sm transition-colors ${
              periodKey === p.key ? 'bg-gray-900 text-white' : 'text-gray-700 hover:bg-gray-50'
            }`}
          >
            {p.label}
          </button>
        ))}
        <button
          onClick={() => {
            onPeriodChange('custom')
            setPickerOpen(true)
          }}
          className={`px-3 py-1.5 text-sm transition-colors border-l border-gray-200 ${
            periodKey === 'custom' ? 'bg-gray-900 text-white' : 'text-gray-700 hover:bg-gray-50'
          }`}
        >
          Период…
        </button>
      </div>

      {periodKey === 'month' && (
        <input
          type="month"
          value={date ?? ''}
          onChange={(e) => onDateChange(e.target.value)}
          className="border border-gray-200 rounded-lg px-2 py-1.5 text-sm"
        />
      )}

      {periodKey === 'quarter' && (
        <div className="flex items-center gap-2">
          <div className="inline-flex items-center rounded-lg border border-gray-200 bg-white">
            <button
              onClick={() => setQuarter(quarter, year - 1)}
              className="px-2 py-1.5 text-gray-500 hover:bg-gray-50 rounded-l-lg"
              title="Предыдущий год"
            >
              ‹
            </button>
            <span className="px-1 text-sm tabular-nums text-gray-900">{year}</span>
            <button
              onClick={() => setQuarter(quarter, year + 1)}
              className="px-2 py-1.5 text-gray-500 hover:bg-gray-50 rounded-r-lg"
              title="Следующий год"
            >
              ›
            </button>
          </div>
          <div className="inline-flex rounded-lg border border-gray-200 bg-white overflow-hidden">
            {QUARTERS.map((q) => (
              <button
                key={q}
                onClick={() => setQuarter(q)}
                className={`px-2.5 py-1.5 text-sm transition-colors ${
                  quarter === q ? 'bg-gray-900 text-white' : 'text-gray-700 hover:bg-gray-50'
                }`}
                title={`${q}-й квартал`}
              >
                Q{q}
              </button>
            ))}
          </div>
        </div>
      )}

      {periodKey === 'year' && (
        <input
          type="number"
          min={USER_DATE_YEAR_MIN}
          max={USER_DATE_YEAR_MAX}
          value={date ?? ''}
          onChange={(e) => onDateChange(e.target.value)}
          className="border border-gray-200 rounded-lg px-2 py-1.5 text-sm w-24"
        />
      )}

      {periodKey === 'custom' && (
        <div className="relative">
          <button
            onClick={() => setPickerOpen((v) => !v)}
            className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 tabular-nums"
          >
            {rangeLabel(from, to)}
          </button>
          {pickerOpen && (
            <DateRangePopover
              from={from}
              to={to}
              onApply={onCustomChange}
              onClose={() => setPickerOpen(false)}
            />
          )}
        </div>
      )}
    </div>
  )
}
